import { defineComponent as _defineComponent } from 'vue'
import {
  onMounted,
  ref,
  watch,
} from 'vue';
import IconDownload from '@/components/svg/pdf-viewer-download.svg?inline';
// import IconPrevious from '@/components/svg/pdf-viewer-previous-page.svg?inline';
// import IconNext from '@/components/svg/pdf-viewer-next-page.svg?inline';
import IconZoomIn from '@/components/svg/pdf-viewer-zoom-in.svg?inline';
import IconZoomOut from '@/components/svg/pdf-viewer-zoom-out.svg?inline';
import IconMaximizeHeight from '@/components/svg/pdf-viewer-maximize-height.svg?inline';
import IconPrint from '@/components/svg/pdf-viewer-print.svg?inline';
import useDrag from '@/mixins/useDrag';
import usePdf from '@/mixins/pdf/usePdf';
import { useDebounceFn, useWindowSize } from '@vueuse/core';
import useResponsiveness from '@/mixins/useResponsiveness';
import BCXIcon from './BCXIcon.vue';
import BCXPopup from './BCXPopup.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'BCXPdfPopup',
  props: {
    pdfBase64: null,
    pdfFilename: null
  },
  emits: ['close'],
  setup(__props: any, { emit }) {

const props = __props as {
  pdfBase64: string;
  pdfFilename: string;
};





const { isMobileWidth } = useResponsiveness();

const popupHeightPx = ref<number>(0);
const popupWidthPx = ref<number>(0);
const popupYPos = ref<number>(0);
const popupXPos = ref<number>(0);

const pdfCanvasContainer = ref<HTMLDivElement | null>(null);
const pdfCanvasWrapper = ref<HTMLDivElement | null>(null);

const { onGrabStart } = useDrag(pdfCanvasContainer);
const {
  downloadPdf,
  fitTo,
  fitToContainer,
  heightIsMaximized,
  loadPdf,
  maximizeHeightOrWidth,
  maxZoomReached,
  minZoomReached,
  printPdf,
  resetPdf,
  zoomIn,
  zoomOut,
} = usePdf(pdfCanvasContainer, pdfCanvasWrapper);

const getWidthFromHeightA4Format = (height: number) => (height / 297) * 210;

const close = () => {
  emit('close');
};

const { width: windowWidth, height: windowHeight } = useWindowSize();

// Calculate optimal popup dimensions and position based on current window size.
// Position updates are sent to BCXPopup, which preserves user's manual positioning
// if they've moved the popup (handled internally by BCXPopup).
const setPopupDimensionsAndPosition = () => {
  popupHeightPx.value = windowHeight.value * 0.95;
  popupYPos.value = windowHeight.value * 0.025;

  const derivedWidth = getWidthFromHeightA4Format(popupHeightPx.value);
  // Mirrors BCXPopup's .popup-content desktop padding (28px). If that value
  // is ever made configurable or removed, this width math breaks silently —
  // other popups (e.g. ProfilePdfUploadPopup) already override .popup-content
  // padding locally to opt out of the wrapper padding.
  const paddingLeftPx = 28;
  const paddingRightPx = 88; // Padding + space for toolbar
  const totalWidth = derivedWidth + paddingLeftPx + paddingRightPx;

  if (totalWidth > windowWidth.value) {
    popupWidthPx.value = windowWidth.value - 50;
    popupXPos.value = 25;
  } else {
    popupWidthPx.value = totalWidth;
    popupXPos.value = (windowWidth.value - totalWidth) / 2;
  }
};

const isFullscreen = ref(false);

const toggleFullscreen = () => {
  isFullscreen.value = !isFullscreen.value;
};

watch(isFullscreen, () => {
  setTimeout(() => {
    if (isFullscreen.value) {
      fitTo('width');
    } else {
      fitToContainer();
    }
  }, 100);
});

let currentAbortController: AbortController | null = null;

const debouncedResetPdf = useDebounceFn(async (pdfBase64: string) => {
  resetPdf();

  if (!pdfBase64) {
    return;
  }

  if (currentAbortController) {
    currentAbortController.abort();
  }

  const controller = new AbortController();
  currentAbortController = controller;

  try {
    await loadPdf(pdfBase64, controller.signal);
  } catch (err: any) {
    if (err.name === 'AbortError') {
      return;
    }
  } finally {
    if (currentAbortController === controller) {
      currentAbortController = null;
    }
  }
}, 100);

watch(() => props.pdfBase64, async (newPdfBase64) => {
  debouncedResetPdf(newPdfBase64);
}, { immediate: true });

const debouncedSetDimensions = useDebounceFn(() => {
  if (popupWidthPx.value > 0 && popupHeightPx.value > 0) {
    setPopupDimensionsAndPosition();
  }
}, 100);

watch([windowWidth, windowHeight], () => {
  debouncedSetDimensions();
}, { immediate: false });

onMounted(async () => {
  setPopupDimensionsAndPosition();
});

return { __sfc: true,props, emit, isMobileWidth, popupHeightPx, popupWidthPx, popupYPos, popupXPos, pdfCanvasContainer, pdfCanvasWrapper, onGrabStart, downloadPdf, fitTo, fitToContainer, heightIsMaximized, loadPdf, maximizeHeightOrWidth, maxZoomReached, minZoomReached, printPdf, resetPdf, zoomIn, zoomOut, getWidthFromHeightA4Format, close, windowWidth, windowHeight, setPopupDimensionsAndPosition, isFullscreen, toggleFullscreen, currentAbortController, debouncedResetPdf, debouncedSetDimensions, IconDownload, IconZoomIn, IconZoomOut, IconMaximizeHeight, IconPrint, BCXIcon, BCXPopup }
}

})